import React, { useState, useEffect } from 'react';
import { Member, RedemptionItem, RedemptionRecord } from '../types';
import {
    saveRedemptionItemsLocal,
    getRedemptionItemsLocal,
    saveRedemptionRecordsLocal,
    getRedemptionRecordsLocal,
    saveMembersLocal
} from '../services/storageService';
import { updateMemberPoints } from '../services/pointsHistoryService';
import { Plus, Trash2, Edit2, ShoppingBag, Clock, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { compressImage } from '../utils/imageCompression';
import {
    uploadShopImage,
    getCheckInImageUrl,
    getCheckInImageUrls,
    deleteCheckInImage,
    getCurrentUser
} from '../services/supabaseClient';
import { syncService } from '../services/syncService';
import { generateUUID } from '../utils/uuid';

interface RedemptionViewProps {
    currentMember: Member;
    onMemberUpdate: (updatedMember: Member) => void;
    memberSwitcher?: React.ReactNode;
}

import { CheckInImage } from './CheckInImage';

export const RedemptionView: React.FC<RedemptionViewProps> = ({ currentMember, onMemberUpdate, memberSwitcher }) => {
    const [items, setItems] = useState<RedemptionItem[]>([]);
    const [records, setRecords] = useState<RedemptionRecord[]>([]);
    const [activeTab, setActiveTab] = useState<'shop' | 'records'>('shop');

    // Admin Mode (Toggle to manage items)
    const [isAdminMode, setIsAdminMode] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<RedemptionItem | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);

    // Form State
    const [formData, setFormData] = useState<Partial<RedemptionItem>>({});

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        const loadedItems = await getRedemptionItemsLocal();
        const loadedRecords = await getRedemptionRecordsLocal();
        // Sort items by cost
        loadedItems.sort((a, b) => a.cost - b.cost);
        // Sort records by time desc
        loadedRecords.sort((a, b) => new Date(b.redeemedAt).getTime() - new Date(a.redeemedAt).getTime());

        setItems(loadedItems);
        setRecords(loadedRecords);

        // Batch Load Shop Images
        const paths = loadedItems
            .map(i => i.image)
            .filter((p): p is string => !!p && !p.startsWith('data:') && !p.startsWith('http'));
        if (paths.length > 0) {
            getCheckInImageUrls(paths);
        }
    };

    const handleSaveItem = async () => {
        if (!formData.title || formData.cost === undefined || formData.stock === undefined) {
            alert("请填写完整信息");
            return;
        }

        setIsUploading(true);
        let imageUrl = formData.image;

        // If a new file was selected, upload it first
        if (selectedFile) {
            const user = await getCurrentUser();
            if (user) {
                const { path, error } = await uploadShopImage(selectedFile, user.id);
                if (error) {
                    alert("图片上传失败: " + error.message);
                    setIsUploading(false);
                    return;
                }
                imageUrl = path;
            }
        }

        let newItems = [...items];
        if (editingItem) {
            // Delete old image if it was a storage path and we replaced it
            if (editingItem.image && editingItem.image !== imageUrl && !editingItem.image.startsWith('data:') && !editingItem.image.startsWith('http')) {
                await deleteCheckInImage(editingItem.image);
            }
            newItems = newItems.map(i => i.id === editingItem.id ? { ...i, ...formData, image: imageUrl } as RedemptionItem : i);
        } else {
            const newItem: RedemptionItem = {
                id: generateUUID(),
                title: formData.title,
                description: formData.description || '',
                cost: Number(formData.cost),
                stock: Number(formData.stock),
                image: imageUrl,
                createdAt: new Date().toISOString()
            };
            newItems.push(newItem);
        }

        await saveRedemptionItemsLocal(newItems);
        setItems(newItems);
        
        const user = await getCurrentUser();
        if (user) {
            syncService.triggerSync(user.id);
        }
        
        setIsFormOpen(false);
        setEditingItem(null);
        setFormData({});
        setSelectedFile(null);
        setIsUploading(false);
    };

    const handleDeleteItem = async (id: string) => {
        const itemToDelete = items.find(i => i.id === id);
        if (!itemToDelete) return;
        if (!confirm("确定要删除这个兑换项吗？")) return;

        // Delete image from storage if it's a path
        if (itemToDelete.image && !itemToDelete.image.startsWith('data:') && !itemToDelete.image.startsWith('http')) {
            await deleteCheckInImage(itemToDelete.image);
        }

        const newItems = items.filter(i => i.id !== id);
        await saveRedemptionItemsLocal(newItems);
        setItems(newItems);
        
        const user = await getCurrentUser();
        if (user) {
            syncService.triggerSync(user.id);
        }
    };

    const handleRedeem = async (item: RedemptionItem) => {
        const cost = item.cost;
        const currentPoints = currentMember.totalPoints || 0;

        if (currentPoints < cost) {
            alert("积分不足！");
            return;
        }

        if (item.stock <= 0) {
            alert("库存不足！");
            return;
        }

        if (!confirm(`确定消耗 ${cost} 积分兑换 "${item.title}" 吗？`)) return;

        // 1. Deduct Points with History
        const updatedMember = updateMemberPoints(currentMember, -cost, 'redemption', {
            reason: `兑换商品: ${item.title}`,
            date: format(new Date(), 'yyyy-MM-dd')
        });

        // 2. Reduce Stock
        const updatedItems = items.map(i => i.id === item.id ? { ...i, stock: i.stock - 1 } : i);

        // 3. Create Record
        const newRecord: RedemptionRecord = {
            id: generateUUID(),
            memberId: currentMember.id,
            itemId: item.id,
            itemName: item.title,
            cost: cost,
            redeemedAt: new Date().toISOString()
        };

        const updatedRecords = [newRecord, ...records];

        // Save All
        await saveMembersLocal([updatedMember]); // Note: In App this needs to sync back, simplistic here
        await saveRedemptionItemsLocal(updatedItems);
        await saveRedemptionRecordsLocal(updatedRecords);

        // Update State
        onMemberUpdate(updatedMember);
        setItems(updatedItems);
        setRecords(updatedRecords);
        
        const user = await getCurrentUser();
        if (user) {
            syncService.triggerSync(user.id);
        }

        alert("兑换成功！");
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            try {
                // Compress image before preview and upload
                const compressed = await compressImage(file, 200); // 200KB limit for shop items
                setSelectedFile(compressed);

                // For preview, Base64 is fine
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFormData({ ...formData, image: reader.result as string });
                };
                reader.readAsDataURL(compressed);
            } catch (err) {
                console.error("Image processing failed:", err);
                alert("图片处理失败");
            }
        }
    };

    return (
        <div className="pb-20 animate-fade-in">
            {/* Fixed Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 -mx-6 px-6 pt-6 pb-4 space-y-4">
                {memberSwitcher}
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800 dark:text-white mt-3">
                        <ShoppingBag className="text-primary-600" /> 积分商城
                    </h1>
                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                        <button
                            onClick={() => setActiveTab('shop')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'shop' ? 'bg-white dark:bg-slate-700 shadow-sm text-primary-600 dark:text-primary-400' : 'text-slate-500 dark:text-slate-400'}`}
                        >
                            兑换
                        </button>
                        <button
                            onClick={() => setActiveTab('records')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'records' ? 'bg-white dark:bg-slate-700 shadow-sm text-primary-600 dark:text-primary-400' : 'text-slate-500 dark:text-slate-400'}`}
                        >
                            记录
                        </button>
                    </div>
                </div>

                {/* Points Display */}
                <div className="bg-gradient-to-r from-amber-200 to-yellow-400 dark:from-amber-900 dark:to-yellow-700 p-4 rounded-xl shadow-lg relative overflow-hidden">
                    <div className="relative z-10 flex justify-between items-center text-amber-900 dark:text-amber-100">
                        <div>
                            <p className="text-sm font-semibold opacity-90">当前可用积分</p>
                            <p className="text-3xl font-bold">{currentMember.totalPoints || 0}</p>
                        </div>
                        <div className="p-2 bg-white/20 rounded-full">
                            <ShoppingBag size={24} />
                        </div>
                    </div>
                    <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/20 rounded-full blur-xl"></div>
                    <div className="absolute -left-4 -top-4 w-16 h-16 bg-white/20 rounded-full blur-lg"></div>
                </div>

                {activeTab === 'shop' && (
                    <div className="flex justify-end">
                        <button
                            onClick={() => setIsAdminMode(!isAdminMode)}
                            className="text-xs text-slate-400 hover:text-primary-500 underline"
                        >
                            {isAdminMode ? '退出管理' : '管理商品'}
                        </button>
                    </div>
                )}
            </div>

            {/* Scrollable Content */}
            <div className="space-y-4 mt-4">
                {activeTab === 'shop' && (
                    <>
                        {isAdminMode && (
                            <button
                                onClick={() => { setEditingItem(null); setFormData({}); setIsFormOpen(true); }}
                                className="w-full py-3 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl text-slate-500 hover:border-primary-500 hover:text-primary-500 transition-colors flex items-center justify-center gap-2"
                            >
                                <Plus size={20} /> 添加兑换项
                            </button>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            {items.map(item => (
                                <div key={item.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden flex flex-col h-full border border-slate-100 dark:border-slate-700">
                                    <div className="relative aspect-square">
                                        <CheckInImage
                                            imagePath={item.image}
                                            alt={item.title}
                                            className="mt-0 h-full w-full rounded-none"
                                        />
                                        {isAdminMode && (
                                            <div className="absolute top-2 right-2 flex gap-1">
                                                <button
                                                    onClick={() => { setEditingItem(item); setFormData(item); setIsFormOpen(true); }}
                                                    className="p-1.5 bg-white/90 rounded-full text-slate-700 shadow-sm hover:text-primary-600"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteItem(item.id)}
                                                    className="p-1.5 bg-white/90 rounded-full text-red-500 shadow-sm hover:bg-red-50"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        )}
                                        {item.stock <= 0 && (
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                                <span className="text-white font-bold px-3 py-1 bg-red-500/80 rounded-full text-sm">已售罄</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="p-3 flex items-start justify-between flex-1 flex-col">
                                        <div className="w-full">
                                            <h3 className="font-bold text-slate-800 dark:text-white truncate">{item.title}</h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 min-h-[2em]">{item.description}</p>
                                        </div>

                                        <div className="w-full mt-3 flex items-end justify-between">
                                            <div>
                                                <span className="text-lg font-bold text-amber-500">{item.cost}</span>
                                                <span className="text-xs text-slate-400 ml-1">积分</span>
                                                <p className="text-[10px] text-slate-400 mt-0.5">剩余: {item.stock}</p>
                                            </div>
                                            <button
                                                onClick={() => handleRedeem(item)}
                                                disabled={item.stock <= 0 || (currentMember.totalPoints || 0) < item.cost}
                                                className="px-3 py-1.5 bg-primary-600 text-white text-xs font-bold rounded-lg shadow-lg shadow-primary-200 dark:shadow-none hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                                            >
                                                兑换
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {items.length === 0 && (
                            <div className="text-center py-10 text-slate-400">
                                <ShoppingBag size={48} className="mx-auto mb-2 opacity-20" />
                                <p>暂无兑换商品</p>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'records' && (
                    <div className="space-y-3">
                        {records.filter(r => r.memberId === currentMember.id).map(record => (
                            <div key={record.id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/20 text-amber-500 rounded-full flex items-center justify-center">
                                        <Clock size={18} />
                                    </div>
                                    <div>
                                        <p className="font-bold text-slate-800 dark:text-white">{record.itemName}</p>
                                        <p className="text-xs text-slate-400">{format(new Date(record.redeemedAt), 'yyyy-MM-dd HH:mm')}</p>
                                    </div>
                                </div>
                                <span className="font-bold text-slate-600 dark:text-slate-400">-{record.cost} 积分</span>
                            </div>
                        ))}

                        {records.filter(r => r.memberId === currentMember.id).length === 0 && (
                            <div className="text-center py-10 text-slate-400">
                                <p>暂无兑换记录</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {isFormOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-2xl p-6 shadow-2xl scale-100 animate-scale-in">
                        <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-4">
                            {editingItem ? '编辑商品' : '添加商品'}
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">商品名称</label>
                                <input
                                    type="text"
                                    value={formData.title || ''}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
                                    placeholder="例如：咖啡兑换券"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase">积分价格</label>
                                    <input
                                        type="number"
                                        value={formData.cost || ''}
                                        onChange={e => setFormData({ ...formData, cost: Number(e.target.value) })}
                                        className="w-full mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
                                        placeholder="0"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase">库存数量</label>
                                    <input
                                        type="number"
                                        value={formData.stock || ''}
                                        onChange={e => setFormData({ ...formData, stock: Number(e.target.value) })}
                                        className="w-full mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
                                        placeholder="99"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">描述 (选填)</label>
                                <textarea
                                    value={formData.description || ''}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-primary-500 dark:text-white"
                                    rows={2}
                                />
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">图片</label>
                                <div className="mt-1 flex items-center gap-4">
                                    {formData.image && (
                                        <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden">
                                            <CheckInImage
                                                imagePath={formData.image}
                                                alt="Preview"
                                                className="mt-0 h-full w-full rounded-none"
                                            />
                                        </div>
                                    )}
                                    <label className="flex-1 cursor-pointer">
                                        <div className="py-2 px-4 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-xl text-sm text-center border border-dashed border-slate-300 hover:bg-slate-200 transition-colors">
                                            点击上传图片
                                        </div>
                                        <input type="file" className="hidden" accept="image/*" onChange={handleImageChange} />
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setIsFormOpen(false)}
                                className="flex-1 py-3 text-slate-500 font-bold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 rounded-xl transition-colors"
                            >
                                取消
                            </button>
                            <button
                                onClick={handleSaveItem}
                                disabled={isUploading}
                                className="flex-1 py-3 bg-primary-600 text-white font-bold rounded-xl shadow-lg shadow-primary-200 dark:shadow-none hover:bg-primary-700 transition-colors disabled:opacity-50"
                            >
                                {isUploading ? '上传中...' : '保存'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
